/**
 * Platform operators (FR-51): who runs the installation, and everything they have done.
 * Operators are named by issuer + subject; email is a label, not a key (design 16.1).
 */
import { useState, type FormEvent } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ControlActivityEntry, PlatformOperator } from "@workcore/schemas";
import { api, checkedWrite, get } from "../api";
import { identity } from "../auth";
import { cfg } from "../config";
import { ErrorBox, Field, PageHeader, Panel, RowMenu, SidePanel, When, when } from "../ui";
import { ACTIONS, describe } from "./WorkspaceDetail";
import { useWorkspaces } from "./Workspaces";
import { confirmAction } from "../confirm";

const PAGE = 100;

function PlatformActivity({ operators }: { operators: PlatformOperator[] }) {
  const workspaces = useWorkspaces().data ?? [];
  const q = useInfiniteQuery({
    queryKey: ["platform-activity"],
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) => z.array(ControlActivityEntry).parse(await get("/v1/admin/activity", { limit: PAGE, before: pageParam })),
    getNextPageParam: (last) => (last.length === PAGE ? last.at(-1)?.id : undefined),
  });
  // Workspace admins and people accepting invitations act here too: name them from the record's own details.
  const who = (e: ControlActivityEntry) => {
    if (e.actorIdentityId === null) return "system";
    const op = operators.find((o) => o.identityId === e.actorIdentityId);
    if (op) return op.displayName;
    const d = e.details as { email?: unknown; by?: unknown };
    if (e.action === "invitation_accepted" && typeof d.email === "string") return d.email;
    if (typeof d.by === "string" && d.by.startsWith("user:")) return "a workspace admin";
    return "a former operator";
  };
  const where = (id: string | null) => (id === null ? "" : workspaces.find((w) => w.id === id)?.name ?? "a removed workspace");
  return (
    <Panel title="Platform activity">
      <ErrorBox error={q.error} />
      <ol className="timeline">
        {q.data?.pages.flat().map((e) => (
          <li key={e.id}>
            <When at={e.at} /> <strong>{who(e)}</strong> {ACTIONS[e.action] ?? e.action}
            {e.workspaceId && <span className="tag">{where(e.workspaceId)}</span>} <span className="muted small">{describe(e)}</span>
          </li>
        ))}
      </ol>
      {q.hasNextPage && <button className="secondary" onClick={() => void q.fetchNextPage()}>Older</button>}
    </Panel>
  );
}

/** "Microsoft Entra" rather than the tenant URL; otherwise the issuer's host. The full value is on hover. */
function issuerName(issuer: string): string {
  if (/^https:\/\/login\.microsoftonline\.com\//.test(issuer)) return "Microsoft Entra";
  try {
    return new URL(issuer).host;
  } catch {
    return issuer;
  }
}

export function OperatorsPage() {
  const queries = useQueryClient();
  const list = useQuery({ queryKey: ["operators"], queryFn: async () => z.array(PlatformOperator).parse(await get("/v1/admin/operators")) });
  const refresh = () => (void queries.invalidateQueries({ queryKey: ["operators"] }), void queries.invalidateQueries({ queryKey: ["platform-activity"] }));
  const [form, setForm] = useState({ issuer: cfg().issuer, subject: "", email: "", displayName: "" });
  const [adding, setAdding] = useState(false);
  const grant = useMutation({
    mutationFn: () => checkedWrite("POST", "/v1/admin/operators", form),
    onSuccess: () => (setForm({ ...form, subject: "", email: "", displayName: "" }), setAdding(false), refresh()),
  });
  const revoke = useMutation({ mutationFn: (id: string) => checkedWrite("DELETE", `/v1/admin/operators/${id}`), onSuccess: refresh });
  const me = identity()?.sub;
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <>
      <PageHeader title="Platform operators" description="Operators create and archive workspaces and appoint each one's first system admin. It gives no access inside any workspace." actions={<button onClick={() => setAdding(true)}>Add operator</button>} />
      <Panel plain>
        <ErrorBox error={list.error ?? revoke.error} />
        <table className="grid">
          <thead><tr><th>Name</th><th>Email</th><th>Sign-in</th><th>Since</th><th /></tr></thead>
          <tbody>
            {list.data?.map((o) => (
              <tr key={o.identityId}>
                <td>{o.displayName}</td>
                <td>{o.email}</td>
                <td className="muted small" title={`${o.subject} at ${o.issuer}`}>{issuerName(o.issuer)} · <code>{o.subject.length > 14 ? `${o.subject.slice(0, 12)}…` : o.subject}</code></td>
                <td><When at={o.grantedAt} /></td>
                <td className="row-actions">
                  {o.subject === me ? (
                    <span className="muted small">you</span>
                  ) : (
                    <RowMenu label={`Actions for ${o.displayName}`} items={[{ label: "Revoke…", danger: true, onSelect: () => void confirmAction({ title: `Revoke ${o.displayName} as platform operator?`, body: "They lose access to platform settings at once: workspaces, operators and the platform log.", action: "Revoke", typed: o.displayName, check: () => api("DELETE", `/v1/admin/operators/${o.identityId}`, { dryRun: true }) }).then((ok) => ok && revoke.mutate(o.identityId)) }]} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <SidePanel title="Add an operator" open={adding} onClose={() => setAdding(false)} onSubmit={() => grant.mutate()} submitLabel="Add operator" busy={grant.isPending} error={grant.error}>
        <p className="muted small">They are matched on issuer and subject when they sign in. The "not a member" page shows both after they first sign in.</p>
        <Field label="Name"><input required value={form.displayName} autoFocus onChange={set("displayName")} /></Field>
        <Field label="Email"><input type="email" required value={form.email} onChange={set("email")} /></Field>
        <Field label="Subject"><input required value={form.subject} onChange={set("subject")} /></Field>
        <Field label="Issuer"><input required value={form.issuer} onChange={set("issuer")} /></Field>
      </SidePanel>
      <PlatformActivity operators={list.data ?? []} />
    </>
  );
}

